import groupBy from 'lodash/groupBy';
import { ListCreator } from './useScorableItemFilters.d';
import { ScorableItemTableDataGroup, ScorableItemTableDataRow } from '../../per-student/hooks/useTableData';

export type CheckboxState = 'checked' | 'unchecked' | 'indeterminate';

type RowsStates = Record<string, CheckboxState>;

export function getParentOfNode(
  nodeId: string,
  flattenedSourceTrees: ScorableItemTableDataGroup[],
): ScorableItemTableDataGroup | null {
  const childNode = flattenedSourceTrees.find(node => node.id === nodeId);
  if (!childNode) return null;

  return flattenedSourceTrees.find(node => node.id === childNode.accumulatedRow.tableGroupId) || null;
}

export function groupsSorterFactory(referenceGroups: ScorableItemTableDataGroup[]) {
  return (group1: ScorableItemTableDataGroup, group2: ScorableItemTableDataGroup) => {
    return (
      referenceGroups.findIndex(group => group.id === group1.id) -
      referenceGroups.findIndex(group => group.id === group2.id)
    );
  };
}

function buildAncestorTreeOfGroup(
  group: ScorableItemTableDataGroup,
  flattenedSourceTrees: ScorableItemTableDataGroup[],
): ScorableItemTableDataGroup[] {
  const groupCopy: ScorableItemTableDataGroup = { ...group, groups: [], rows: [] };

  const tree: ScorableItemTableDataGroup[] = [groupCopy];

  function traverseAncestors(childGroup: ScorableItemTableDataGroup): void {
    if (!childGroup.accumulatedRow.tableGroupId) return;

    const parentGroup = getParentOfNode(childGroup.id, flattenedSourceTrees);
    if (!parentGroup) return;

    const parentGroupCopy: ScorableItemTableDataGroup = { ...parentGroup, groups: [], rows: [] };

    tree.unshift(parentGroupCopy);
    traverseAncestors(parentGroup);
  }

  traverseAncestors(group);

  return tree;
}

interface Opts {
  newRowStates: RowsStates;
  allTableRows: ScorableItemTableDataRow[];
  allTableGroups: ScorableItemTableDataGroup[];
  filteredTableGroups: ScorableItemTableDataGroup[];
}

export function listCreatorFactory({
  allTableGroups,
  allTableRows,
  newRowStates,
  filteredTableGroups,
}: Opts): ListCreator {
  const checkedRowIds = Object.entries(newRowStates)
    .filter(([, state]) => state === 'checked')
    .map(([id]) => id);

  const checkedRows = allTableRows.filter(item => checkedRowIds.includes(item.id));
  const rowsPerGroup = groupBy(checkedRows, 'tableGroupId');

  const uniqueGroupIdsOfRows = new Set(
    checkedRowIds.map(id => allTableRows.find(item => item.id === id)?.tableGroupId),
  );

  const uniqueGroups = Array.from(uniqueGroupIdsOfRows)
    .map(id => allTableGroups.find(group => group.id === id))
    .filter(Boolean) as ScorableItemTableDataGroup[];

  const ancestorTrees = uniqueGroups.reduce<ScorableItemTableDataGroup[][]>((acc, group) => {
    const ancestorTree = buildAncestorTreeOfGroup(group, filteredTableGroups);
    acc.push(ancestorTree);
    return acc;
  }, []);

  const listCreator: ListCreator = tableDataGroups => {
    const res = ancestorTrees.reduce((acc, ancestorTree) => {
      let lastGroup: ScorableItemTableDataGroup | null = null;

      ancestorTree.forEach((ancestorGroup, index) => {
        if (lastGroup && !lastGroup.groups) lastGroup.groups = [];
        if (lastGroup && !lastGroup.rows) lastGroup.rows = [];

        if (lastGroup === null) {
          const rootGroup = tableDataGroups.find(group => group.id === ancestorTree[0].id);
          if (!rootGroup) {
            acc.push(ancestorGroup);
            lastGroup = ancestorGroup;
          } else {
            lastGroup = rootGroup;
          }
        } else if (lastGroup && lastGroup.groups?.some(group => group.id === ancestorGroup.id)) {
          lastGroup = lastGroup.groups.find(group => group.id === ancestorGroup.id)!;
        } else if (lastGroup && !lastGroup.groups?.some(group => group.id === ancestorGroup.id)) {
          lastGroup.groups!.push(ancestorGroup);
          lastGroup = ancestorGroup;
        }

        if (!lastGroup) return acc;

        const originalGroup = allTableGroups.find(node => node.id === lastGroup!.id);
        if (!originalGroup) return acc;

        if (originalGroup.groups) {
          lastGroup.groups?.sort(groupsSorterFactory(originalGroup.groups));
        }

        const isLast = index === ancestorTree.length - 1;
        if (isLast && rowsPerGroup[ancestorGroup.id]) {
          const rowsToAdd = (rowsPerGroup[ancestorGroup.id] || []).filter(row => {
            return !lastGroup?.rows?.some(existingRow => existingRow.id === row.id);
          });

          const rowsForLastNode = [...(lastGroup.rows || []), ...rowsToAdd].sort((row1, row2) => {
            return (
              originalGroup.rows?.findIndex(row => row.id === row1.id)! -
              originalGroup.rows?.findIndex(row => row.id === row2.id)!
            );
          });

          lastGroup.rows = rowsForLastNode;
        }
      });

      return acc;
    }, tableDataGroups);

    return res.sort(groupsSorterFactory(tableDataGroups));
  };

  return listCreator;
}
